<template>
  <v-card>
    <v-card-title primary-title>
      <div class="headline">
        Edit Device Details
      </div>
    </v-card-title>
    <v-card-text>
      <v-form ref="editForm">
        <v-container
          grid-list-lg
          fluid
          class="pa-3"
        >
          <v-layout
            row
            wrap
          >
            <v-flex
              xs12
              sm6
              class="d-flex align-end"
            >
              <v-text-field
                v-model="selectedDevice.name"
                :rules="[v => !!v || 'Name is required']"
                name="name"
                label="Name"
              />
            </v-flex>
            <v-flex
              v-if="selectedDevice.deviceOS !== 'PRINTER'"
              xs12
              sm6
            >
              <v-select
                v-model="selectedDevice.printers"
                :items="printers"
                :rules="[v => !!v || 'Printer is required']"
                label="Paired Printers"
                chips
                deletable-chips
                item-text="name"
                item-value="id"
                multiple
              />
            </v-flex>
            <v-flex
              xs12
              sm6
            >
              <v-select
                v-if="selectedDevice.model"
                v-model="selectedDevice.model"
                :items="printerModels"
                :rules="[v => !!v || 'Model is required']"
                label="Select Printer Model"
                disabled
              />
            </v-flex>
            <template v-if="selectedDevice.model">
              <template v-if="selectedDevice.model === 'QL-820NWB'">
                <v-flex
                  xs12
                  sm6
                >
                  <p class="subheading mb-0">
                    Connectivity Interface
                  </p>
                  <v-radio-group
                    v-model="selectedDevice.interface"
                    :rules="[v => !!v || 'Connectivity type is required for the selected printer']"
                    disabled
                    row
                  >
                    <v-radio
                      label="Bluetooth"
                      value="BLUETOOTH"
                      color="primary"
                    />
                    <v-radio
                      label="Wifi"
                      value="WIFI"
                      color="primary"
                    />
                  </v-radio-group>
                </v-flex>
                <v-flex
                  v-if="selectedDevice.interface === 'BLUETOOTH'"
                  xs12
                  sm6
                >
                  <v-text-field
                    v-model="selectedDevice.bmac"
                    :rules="[v => {return validateMac(v, 'required')}]"
                    :validate-on-blur="true"
                    label="Bluetooth MAC Address"
                    return-masked-value
                    disabled
                    mask="NN:NN:NN:NN:NN:NN"
                  />
                </v-flex>
              </template>
              <template v-if="selectedDevice.model !== 'QL-820NWB' || selectedDevice.interface === 'WIFI'">
                <v-flex
                  xs12
                  sm6
                >
                  <v-text-field
                    v-model="selectedDevice.ip"
                    :rules="[v => !!v || 'IP Address is required', v => ipRegex.test(v) || 'Invalid IP Address' ]"
                    :validate-on-blur="true"
                    label="IP Address"
                    placeholder="106.51.36.97"
                  />
                </v-flex>
              </template>
              <template v-if="selectedDevice.interface === 'WIFI' || selectedDevice.model !== 'QL-820NWB'">
                <v-flex
                  xs12
                  sm6
                >
                  <v-text-field
                    v-model="selectedDevice.wmac"
                    :rules="[v => {return validateMac(v, 'notrequired')}]"
                    :validate-on-blur="true"
                    :class="{addNegMargin:(device.model === 'QL-820NWB' && device.interface === 'WIFI')}"
                    label="Wifi MAC Address"
                    return-masked-value
                    disabled
                    mask="NN:NN:NN:NN:NN:NN"
                  />
                </v-flex>
              </template>
            </template>
          </v-layout>
        </v-container>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        flat
        color="primary"
        :disabled="updateLoader"
        @click.stop="cancelDialog()"
      >
        Cancel
      </v-btn>
      <v-btn
        flat
        color="primary"
        :loading="updateLoader"
        @click.stop="updateDeviceDetails"
      >
        Update
      </v-btn>
    </v-card-actions>
  </v-card>
</template>
<script>
import _cloneDeep from 'lodash/cloneDeep';
import _without from 'lodash/without';
import _omit from 'lodash/omit';
import _get from 'lodash/get';
import { database } from '@/firebase';

export default {
  name: 'EditDevice',
  props: {
    device: {
      type: Object,
      default: () => {},
      required: true
    },
    devices: {
      type: Array,
      default: () => [],
      required: true
    },
    kiosks: {
      type: Array,
      default: () => [],
      required: true
    },
    printers: {
      type: Array,
      default: () => {},
      required: true
    }
  },
  data() {
    return {
      selectedDevice: {},
      fanoutPrinters: {},
      printersObj: {},
      promises: [],
      updateLoader: false,
      ipRegex: /^\b(?:(?:25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9]?[0-9])\.){3}(?:25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9]?[0-9])\b$/,
      printerModels: ['QL-820NWB', 'QL-720NW']
    };
  },
  computed: {
    deviceRef() {
      const lid = _get(this.$store.state.VMS, 'lid');
      // return null if the location ID is not yet set
      if (!lid) return null;

      return database.ref(`vms/devices`);
    }
  },
  watch: {
    device: {
      handler(val) {
        if (!val) return;
        this.selectedDevice = _cloneDeep(val);
      },
      immediate: true
    },
    printers: {
      handler(val) {
        if (!val) return;
        this.printersObj = this.arrayToObject(val);
      },
      immediate: true
    }
  },
  methods: {
    validateMac(val, type) {
      let duplicate = false;
      if (type === 'notrequired' && val) {
        if (/^([0-9a-f]{2}([:-]|$)){6}$|([0-9a-f]{4}([.]|$)){3}$/i.test(val)) {
          const filtered = this.devices.filter(
            device => device.id !== this.selectedDevice.id
          );
          filtered.forEach(device => {
            if (device.wmac && device.wmac === val) duplicate = true;
          });
          if (!duplicate) return true;
          return 'Duplication MAC Address';
        }
        return 'MAC is invlaid';
      } else if (type === 'required') {
        if (val) {
          if (
            /^([0-9a-f]{2}([:-]|$)){6}$|([0-9a-f]{4}([.]|$)){3}$/i.test(val)
          ) {
            const filtered = this.devices.filter(
              device => device.id !== this.selectedDevice.id
            );
            filtered.forEach(device => {
              if (device.bmac && device.bmac === val) duplicate = true;
            });
            if (!duplicate) return true;
            return 'Duplicate MAC Address';
          }
          return 'Mac is invalid';
        }
        return 'Mac is required';
      }
      return true;
    },
    arrayToObject(array) {
      return array.reduce((obj, item) => {
        const key = item.id;
        const value = _omit(item, ['id']);
        // eslint-disable-next-line
        obj[key] = value;
        return obj;
      }, {});
    },
    cancelDialog() {
      this.$emit('closeDialog');
      this.$refs.editForm.reset();
    },
    updateDeviceDetails() {
      if (!this.$refs.editForm.validate()) return;
      this.updateLoader = true;
      this.$emit('persistDialog');
      const device = _cloneDeep(this.selectedDevice);
      const deviceUpdatePromise = this.deviceRef
        .child(device.id)
        .update(device);
      this.promises.push(deviceUpdatePromise);
      if (this.selectedDevice.deviceOS !== 'PRINTER') {
        this.fanoutDeviceDetails();
      } else {
        this.resolvePromises();
      }
    },
    fanoutDeviceDetails() {
      this.fanoutPrinters = {};
      // console.log(this.printersObj, this.device);
      const removed = (this.device.printers || []).filter(
        pid => !this.selectedDevice.printers.includes(pid)
      );
      const added = this.selectedDevice.printers.filter(
        pid => !(this.device.printers || []).includes(pid)
      );

      removed.forEach(pid => {
        this.fanoutPrinters[`${pid}/kiosks`] = _without(
          this.printersObj[pid].kiosks,
          this.device.id
        );
      });

      added.forEach(pid => {
        if ((this.printersObj[pid].kiosks || []).includes(this.device.id))
          return;
        this.fanoutPrinters[`${pid}/kiosks`] = [
          ...(this.printersObj[pid].kiosks || []),
          this.device.id
        ];
      });
      const fanoutPrinterPromise = this.deviceRef.update(this.fanoutPrinters);
      this.promises.push(fanoutPrinterPromise);
      this.resolvePromises();
    },
    resolvePromises() {
      Promise.all(this.promises)
        .then(() => {
          this.$emit('closeDialog');
          this.$emit(
            'success',
            'The device details has been updated successfully'
          );
          this.$refs.editForm.reset();
          this.updateLoader = false;
        })
        .catch(error => {
          this.$emit('closeDialog');
          this.$emit('error', error);
          this.$refs.editForm.reset();
          this.updateLoader = false;
        });
    }
  }
};
</script>
